import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Flex, Stack, TFCard, TFText } from '@tf/ui';
import type React from 'react';

import { ActionButton, useGetConnectionLabels } from '@/components/shared';
import { ConnectedEntitiesList } from '@/components/structure/ConnectedEntitiesList';
import { useModalsController } from '@/components/structure/ModalsControllerContext';
import type { ConnectedEntity, Connection, StructureEntity } from '@/components/structure/useStructureEntities';

interface Props {
	otherConnections: Connection[];
	mainEntity: StructureEntity;
	entities: StructureEntity[];
}

type ConnectedEntityWithKind = ConnectedEntity & { connectionKind: string };

export const AddOtherConnections: React.FC<Props> = ({ otherConnections, mainEntity, entities }) => {
	const { _ } = useLingui();
	const { addConnectionModal } = useModalsController() ?? {};
	const getConnectionLabels = useGetConnectionLabels();

	const connectedEntities = otherConnections.reduce<ConnectedEntityWithKind[]>((acc, curr) => {
		for (const connection of curr.connectedEntities) {
			acc.push({ ...connection, connectionKind: curr.connectionKind });
		}
		return acc;
	}, []);

	return (
		<TFCard w="100%" innerPadding="1.5rem">
			<TFText fw="500" fz="14px" lh="18px" mb="12px">
				<Trans id="structure.addOtherConnections.label">
					Are there any other participants in your business? Please add all relevant individuals or entities.
				</Trans>
			</TFText>
			<Stack gap="12px">
				<ConnectedEntitiesList
					mainEntity={mainEntity}
					connectedEntities={connectedEntities}
					entities={entities}
					labelsToShow="connectionKind"
				/>
				<Flex wrap="wrap" gap="12px">
					{otherConnections.map(({ connectionKind }) => {
						const getLabelsForEntity = getConnectionLabels(mainEntity.entityKind);
						const connectionLabelSingular = getLabelsForEntity(connectionKind)?.singular;
						return (
							<ActionButton
								size="sm"
								style={{ width: 'fit-content' }}
								key={connectionKind}
								title={_(
									msg({
										id: 'structure.addOtherConnections.addConnectionButton.label',
										message: `Add ${connectionLabelSingular}`,
									}),
								)}
								onClick={() =>
									addConnectionModal?.open({
										connectionKind,
										parentEntityKind: mainEntity.entityKind,
										parentEntityName: mainEntity.entityName,
										parentEntityId: mainEntity.entityId,
										withSuccessMessage: false,
									})
								}
							/>
						);
					})}
				</Flex>
			</Stack>
		</TFCard>
	);
};
